import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import { ButtonOutlined } from '../../UI';
import { mapearDocumentos } from './documentosClienteController';
import {
  buscarDocumentosClientePorIdClienteService,
  buscarArquivoDocumentoClientePorIdDocumentoService,
} from '../../../../services/core/cliente/cliente.services';
import { downloadFile, openPagePDF } from '../../../../helpers/utils/utils';

const OPCOES_TAMANHO_PAGINA = [5, 10, 15];

/**
 * Popup "Visualizar Documento" (AW2-1136 Etapa 5, AC1) — porta de
 * `DocumentosCliente.jsx` real no modo `isDisplayMode=true` (produção já
 * tem essa variante pronta: some "Novo"/"Editar"/"Excluir", mantém só
 * "Baixar"/"Visualizar" — exatamente o alvo desta etapa). Sem "Novo Acordo"
 * nem qualquer mutação — puramente somente-leitura.
 *
 * Fetch dispara ao abrir (`open` true) e a cada troca de página/tamanho de
 * página enquanto aberto — sem guard de sessão de mutação (não há mutação
 * aqui) — mesmo padrão simples de "buscar quando aberto" já usado por
 * outros dialogs de consulta do módulo.
 *
 * "Visualizar" só habilitado para PDF (`hasNotPdf` real) — outros formatos
 * (imagem/Word/Excel) só têm "Baixar", mesma regra de produção real
 * (`DocumentosCliente.jsx`, `hasNotPdf = extensao?.toUpperCase() !== 'PDF'`).
 */
export default function DialogDocumentosCliente({ open, idCliente, onClose }) {
  const [documentos, setDocumentos] = useState({
    content: [], size: 5, page: 0, totalElements: 0,
  });
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!open || !idCliente) {
      return;
    }

    setCarregando(true);
    buscarDocumentosClientePorIdClienteService(idCliente, { page: documentos.page, size: documentos.size })
      .then(({ data }) => {
        setDocumentos(mapearDocumentos(data));
      })
      .catch(() => {
        setErro('Ocorreu um problema ao tentar buscar os documentos');
      })
      .finally(() => {
        setCarregando(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idCliente, documentos.page, documentos.size]);

  const onChangePagina = (event, novaPagina) => {
    setDocumentos(atual => ({ ...atual, page: novaPagina }));
  };

  const onChangeTamanhoPagina = (event) => {
    setDocumentos(atual => ({ ...atual, size: Number(event.target.value), page: 0 }));
  };

  const onClickBaixar = documento => async () => {
    try {
      const { data } = await buscarArquivoDocumentoClientePorIdDocumentoService(documento.idClienteDocumento);
      downloadFile(documento.nomeDocumento, data);
    } catch {
      setErro('Não existem documentos anexados ao Cliente');
    }
  };

  const onClickVisualizar = documento => async () => {
    try {
      const { data } = await buscarArquivoDocumentoClientePorIdDocumentoService(documento.idClienteDocumento);
      openPagePDF(data);
    } catch {
      setErro('Não existem documentos anexados ao Cliente');
    }
  };

  const onFecharErro = () => setErro('');

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Documentos do Cliente</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Extensão</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Nome do Arquivo</TableCell>
                <TableCell align="center">Original Arquivado</TableCell>
                <TableCell>Data de Arquivamento</TableCell>
                <TableCell>Responsável</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentos.content.map((documento) => {
                const naoEhPdf = (documento.extensao || '').toUpperCase() !== 'PDF';
                return (
                  <TableRow key={documento.idClienteDocumento} hover>
                    <TableCell>{documento.extensao.toUpperCase()}</TableCell>
                    <TableCell>{documento.tipo}</TableCell>
                    <TableCell>{documento.descricao}</TableCell>
                    <TableCell>{documento.nomeDocumento}</TableCell>
                    <TableCell align="center">
                      {documento.isDocumentoOriginal ? <CheckCircleIcon fontSize="small" /> : ''}
                    </TableCell>
                    <TableCell>{documento.dataArquivamento}</TableCell>
                    <TableCell>{documento.nomeUsuario}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Baixar">
                        <IconButton size="small" onClick={onClickBaixar(documento)}>
                          <CloudDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={naoEhPdf ? '' : 'Visualizar'}>
                        <span>
                          <IconButton size="small" onClick={onClickVisualizar(documento)} disabled={naoEhPdf}>
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!carregando && documentos.content.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">Nenhum documento encontrado</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {documentos.totalElements > 0 && (
            <TablePagination
              component="div"
              count={documentos.totalElements}
              page={documentos.page}
              rowsPerPage={documentos.size}
              rowsPerPageOptions={OPCOES_TAMANHO_PAGINA}
              onPageChange={onChangePagina}
              onRowsPerPageChange={onChangeTamanhoPagina}
              labelRowsPerPage="Linhas por página:"
            />
          )}
        </DialogContent>
        <DialogActions>
          <ButtonOutlined onClick={onClose}>Fechar</ButtonOutlined>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={onFecharErro}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={onFecharErro}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogDocumentosCliente.propTypes = {
  open: PropTypes.bool,
  idCliente: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func,
};

DialogDocumentosCliente.defaultProps = {
  open: false,
  idCliente: null,
  onClose: () => null,
};
